import React, { useState, useEffect } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import ContentfulToggle from 'components/Contentful/ContentfulToggle';
import Select from 'components/Core/Select';
import Accordion from '@servicetitan/website-common/src/Components/Accordion';
import { ReactComponent as Map } from './map.svg';
import LineGraph from './LineGraph';
import useBreakpointView from 'utils/useBreakpointView';

const dayjs = require('dayjs');

const StateOfTheTradesReport = ({}) => {
  const [mapFilters, setMapFilters] = useState({
    period: undefined,
    metric: 'TICKET_SIZE_INCREASE',
    market: 'all',
  });
  const [mapData, setMapData] = useState([]);
  const [graphFilters, setGraphFilters] = useState({
    market: 'all',
  });
  const { allTradesDataPeriod } = useStaticQuery(graphql`
    query {
      allTradesDataPeriod {
        nodes {
          CLIMATE_REGION
          DATE
          MARKET_CATEGORY
          TICKET_SIZE_INCREASE
        }
      }
    }
  `);
  const data = allTradesDataPeriod.nodes;

  const isMobile = useBreakpointView(['xs', 'sm']);
  const monthOptions = [
    ...[...new Set(data.map((x) => x.DATE))]
      .map((x) => ({ label: dayjs(x).format('MMMM YYYY'), value: x }))
      .reverse(),
  ];
  const marketOptions = [
    { label: 'All', value: 'all' },
    { label: 'Residential', value: 'Residential' },
    { label: 'Commercial', value: 'Commercial Plus' },
  ];
  const metricOptions = [
    // { label: 'Revenue', value: 'REVENUE_INCREASE' },
    // { label: 'Jobs', value: 'JOBS_INCREASE' },
    { label: 'Avg Ticket', value: 'TICKET_SIZE_INCREASE' },
  ];
  const regions = {
    Northeast: {
      id: 'ne',
      states: ['CT', 'DC', 'DE', 'MA', 'MD', 'ME', 'NH', 'NJ', 'NY', 'PA', 'RI', 'VT'],
    },
    'Northern Rockies and Plains': {
      id: 'roc',
      states: ['MT', 'ND', 'NE', 'SD', 'WY'],
    },
    Northwest: {
      id: 'nw',
      states: ['AK', 'ID', 'OR', 'WA'],
    },
    'Ohio Valley': {
      id: 'oh',
      states: ['IL', 'IN', 'KY', 'MO', 'OH', 'TN', 'WV'],
    },
    South: {
      id: 's',
      states: ['AR', 'KS', 'LA', 'MS', 'OK', 'TX'],
    },
    Southeast: {
      id: 'se',
      states: ['AL', 'FL', 'GA', 'NC', 'SC', 'VA'],
    },
    Southwest: {
      id: 'sw',
      states: ['AZ', 'CO', 'NM', 'UT'],
    },
    'Upper Midwest': {
      id: 'umw',
      states: ['IA', 'MI', 'MN', 'WI'],
    },
    West: {
      id: 'w',
      states: ['CA', 'HI', 'NV'],
    },
  };
  const colorRanges = [
    {
      max: 100,
      min: 11,
      color1: '#8bb1e8',
      color2: '#0265DC80',
      color3: 'text-[#0265DC]',
    },
    {
      max: 10,
      min: 4,
      color1: '#b9d0f2',
      color2: '#0265DC80',
      color3: 'text-[#0265DC]',
    },
    {
      max: 3,
      min: -3,
      color1: '#eeeeee',
      color2: '#949596',
      color3: 'text-[#949596]',
    },
    {
      max: -4,
      min: -10,
      color1: '#efcccb',
      color2: '#E24F4F50',
      color3: 'text-[#E24F4F]',
    },
    {
      max: -11,
      min: -100,
      color1: '#e6aaa9',
      color2: '#E24F4F80',
      color3: 'text-[#E24F4F]',
    },
  ];

  const allFilter = (x) =>
    x.MARKET_CATEGORY === (graphFilters.market || 'all') && x.CLIMATE_REGION === 'all';

  const mapDataField = (x) => ({
    DATE: new Date(x.DATE).getTime(),
    CLIMATE_REGION: x.CLIMATE_REGION,
    ...metricOptions.reduce((prev, cur) => {
      prev[cur.value] = parseInt(parseFloat(x[cur.value]).toFixed(2) * 100);
      return prev;
    }, {}),
  });

  const applyFilters = (x) =>
    x.CLIMATE_REGION !== 'all' &&
    x.DATE === mapFilters.period &&
    x.MARKET_CATEGORY === mapFilters.market;

  useEffect(() => {
    const filtered = data
      ?.filter(applyFilters)
      ?.map((d) => ({
        region: d.CLIMATE_REGION,
        value: parseInt(parseFloat(d[mapFilters.metric]) * 100),
      }))
      ?.toSorted((b, a) => (a.value < b.value ? 1 : a.value > b.value ? -1 : 0));

    if (!isMobile) {
      filtered?.forEach((d, i) => {
        const r = regions?.[d?.region];

        if (r?.id) {
          const color = colorRanges.find((x) => d.value >= x.min && d.value <= x.max);

          if (color) {
            document.querySelector(`svg #${r.id}_region > *`)?.setAttribute('fill', color.color1);
            document.querySelector(`svg #${r.id}_states`)?.setAttribute('fill', color.color1);
            document
              .querySelector(`svg #${r.id.toUpperCase()}_HOLDER`)
              ?.setAttribute('fill', color.color2);
          }

          document.querySelector(`svg #${r.id.toUpperCase()}_VALUE`).innerHTML = `${d.value}% Y/Y`;

          const valueWidth = d.value.toString().length;
          const holderWidth = valueWidth === 1 ? '99' : valueWidth === 2 ? '112' : '122';

          document
            .querySelector(`svg #${r.id.toUpperCase()}_HOLDER`)
            .setAttribute('width', holderWidth);

          const show = (e) => {
            document.querySelector(`svg #${r.id}_region .region`).style['opacity'] = 'unset';
            document.querySelector(`svg #${r.id}_states`).style['opacity'] = '0';
            document.querySelector(`svg #${r.id}_tooltip`).style['display'] = 'inline';
            document.querySelector(`svg #${r.id}_region`).style['filter'] =
              'drop-shadow(1px 2px 4px #00000050)';
            e.stopPropagation();
          };

          const hide = (e) => {
            document.querySelector(`svg #${r.id}_region .region`).style['opacity'] = '0';
            document.querySelector(`svg #${r.id}_states`).style['opacity'] = 'unset';
            document.querySelector(`svg #${r.id}_tooltip`).style['display'] = 'none';
            document.querySelector(`svg #${r.id}_region`).style['filter'] = 'none';
            e.stopPropagation();
          };

          document
            .querySelector(`svg #${r.id.toUpperCase()}_HOLDER`)
            .addEventListener('mouseover', show);
          document
            .querySelector(`svg #${r.id.toUpperCase()}_HOLDER`)
            .addEventListener('mouseout', hide);

          document
            .querySelector(`svg #${r.id.toUpperCase()}_VALUE`)
            .addEventListener('mouseover', show);
          document
            .querySelector(`svg #${r.id.toUpperCase()}_VALUE`)
            .addEventListener('mouseout', hide);

          document.querySelector(`svg #${r.id}_region`).addEventListener('mouseover', show);
          document.querySelector(`svg #${r.id}_region`).addEventListener('mouseout', hide);

          document.querySelector(`svg #${r.id}_tooltip`).addEventListener('mouseover', show);
          document.querySelector(`svg #${r.id}_tooltip`).addEventListener('mouseout', hide);

          const stats = document.querySelector(`svg #${r.id}_tooltip .stats`);
          if (stats) {
            const metric = metricOptions.find((x) => x.value === mapFilters.metric).label;
            stats.innerHTML = `${metric}: ${d.value}% Y/Y`;
          }
        }
      });
    }

    setMapData(data?.filter(applyFilters).map(mapDataField));
  }, [data, mapFilters, isMobile]);

  const MarketFilter = (props) => (
    <Select
      placeholder="Residential/Commercial"
      className="h-[56px]"
      options={marketOptions}
      id="market"
      values={mapFilters}
      {...props}
    />
  );

  const MetricFilter = (props) => (
    <Select
      placeholder="Growth Metric"
      className="h-[56px]"
      options={metricOptions}
      id="metric"
      values={mapFilters}
      {...props}
    />
  );

  const MonthFilter = (props) => (
    <Select
      placeholder="Month"
      className="h-[56px]"
      options={monthOptions}
      id="period"
      values={mapFilters}
      defaultValue={monthOptions[0].value}
      {...props}
    />
  );

  if (!mapFilters.period) {
    mapFilters.period = monthOptions[0].value;
  }

  return (
    <div>
      {isMobile && (
        <div className="flex flex-col gap-[30px]">
          <div className="flex flex-col gap-[20px]">
            <MonthFilter
              handleChange={(id, value) => setMapFilters({ ...mapFilters, [id]: value })}
            />
            <MarketFilter
              handleChange={(id, value) => setMapFilters({ ...mapFilters, [id]: value })}
            />
          </div>
          <div className="flex gap-[20px] flex-col">
            {mapData.length > 0 &&
              Object.keys(regions).map((region, i) => {
                const d = mapData.find((x) => x.CLIMATE_REGION === region);

                if (!d) {
                  return <></>;
                }

                return (
                  <Accordion
                    id={`accordion-${regions[region].id}`}
                    className="w-full"
                    HeadingComponent={() => (
                      <div className="w-full flex flex-col text-left items-start gap-0">
                        <h3 className="mb-[10px]">{region}</h3>
                        <p className="mb-0 text-[12px] text-[#737475]">
                          {regions[region].states.join(', ')}
                        </p>
                      </div>
                    )}
                    DetailsComponent={() => (
                      <div className="w-full flex flex-row flex-wrap">
                        {metricOptions.map((o) => {
                          const color = colorRanges.find(
                            (x) => d[o.value] >= x.min && d[o.value] <= x.max,
                          );
                          return (
                            <>
                              <div className="w-3/5 text-mobile-h5 font-extrabold">{o.label}:</div>
                              <div className={`w-2/5 text-right font-extrabold ${color.color3}`}>
                                {d[o.value]}% Y/Y
                              </div>
                            </>
                          );
                        })}
                      </div>
                    )}
                  />
                );
              })}
          </div>
        </div>
      )}
      {!isMobile && (
        <ContentfulToggle
          startingIndex={0}
          items={[
            <div className="mt-[40px] flex flex-col gap-[40px]">
              <div className="flex flex-col gap-[10px] md:flex-row">
                <MonthFilter
                  handleChange={(id, value) => setMapFilters({ ...mapFilters, [id]: value })}
                />
                <MetricFilter
                  handleChange={(id, value) => setMapFilters({ ...mapFilters, [id]: value })}
                />
                <MarketFilter
                  handleChange={(id, value) => setMapFilters({ ...mapFilters, [id]: value })}
                />
              </div>
              <div className="full">
                <Map className="full hidden md:block" />
              </div>
            </div>,
            <div className="mt-[40px] flex flex-col gap-[40px]">
              <div className="flex justify-end">
                <div className="w-full md:w-[200px]">
                  <MarketFilter
                    handleChange={(id, value) => setGraphFilters({ ...graphFilters, [id]: value })}
                    values={graphFilters}
                  />
                </div>
              </div>
              <div>
                <LineGraph
                  seriesData={[
                    // {
                    //   name: 'Revenue',
                    //   color: '#000000',
                    //   marker: {
                    //     symbol: 'circle',
                    //   },
                    //   data: data
                    //     .filter(allFilter)
                    //     .map(mapDataField)
                    //     .map((x) => [x.DATE, x.REVENUE_INCREASE]),
                    // },
                    // {
                    //   name: 'Jobs',
                    //   color: '#0265DC',
                    //   marker: {
                    //     symbol: 'circle',
                    //   },
                    //   data: data
                    //     .filter(allFilter)
                    //     .map(mapDataField)
                    //     .map((x) => [x.DATE, x.JOBS_INCREASE]),
                    // },
                    {
                      name: 'Ticket Size',
                      color: '#18A761',
                      marker: {
                        symbol: 'circle',
                      },
                      data: data
                        .filter(allFilter)
                        .map(mapDataField)
                        .map((x) => [x.DATE, x.TICKET_SIZE_INCREASE]),
                    },
                  ]}
                />
              </div>
            </div>,
          ]}
          hideClass="hidden"
          titles={['Map', 'Line Chart']}
        />
      )}
    </div>
  );
};

export default StateOfTheTradesReport;
